<template>
  <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
      <div>
        <h2 class="text-lg font-bold text-neutral-900">操作日志</h2>
        <p class="text-sm text-neutral-500 mt-1">
          记录登录、下载 / 在线使用、评论、反馈、改密等关键操作，留存不少于 6 个月，用于安全追溯
        </p>
      </div>
      <button
        @click="getList"
        class="px-4 py-2 rounded-lg border border-neutral-300 text-sm text-neutral-600 hover:bg-neutral-50"
      >
        <i class="fa fa-refresh mr-1"></i>刷新
      </button>
    </div>

    <!-- 筛选 -->
    <div class="flex flex-wrap items-center gap-3 mb-4">
      <input
        v-model="searchKey"
        @keyup.enter="reload"
        placeholder="搜索操作人 / 内容 / IP"
        class="flex-1 min-w-[220px] px-3 py-2 rounded-lg border border-neutral-300 focus:border-primary outline-none text-sm"
      />
      <select
        v-model="type"
        @change="reload"
        class="px-3 py-2 rounded-lg border border-neutral-300 focus:border-primary outline-none text-sm text-neutral-700"
      >
        <option value="">全部类型</option>
        <option v-for="t in types" :key="t.v" :value="t.v">{{ t.n }}</option>
      </select>
      <button
        @click="reload"
        class="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90"
      >
        查询
      </button>
    </div>

    <!-- 表格 -->
    <div v-if="loading" class="py-16 text-center text-neutral-400 text-sm">加载中…</div>
    <div v-else-if="!list.length" class="py-16 text-center text-neutral-400 text-sm">
      暂无日志记录
    </div>
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-neutral-50 text-neutral-600 text-left">
            <th class="py-3 px-3 font-medium whitespace-nowrap">时间</th>
            <th class="py-3 px-3 font-medium whitespace-nowrap">操作人</th>
            <th class="py-3 px-3 font-medium whitespace-nowrap">操作类型</th>
            <th class="py-3 px-3 font-medium">详情</th>
            <th class="py-3 px-3 font-medium whitespace-nowrap">IP</th>
            <th class="py-3 px-3 font-medium whitespace-nowrap">结果</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="log in list" :key="log.id" class="border-b border-neutral-100 hover:bg-neutral-50/60">
            <td class="py-3 px-3 text-neutral-400 text-xs whitespace-nowrap">{{ log.createTime }}</td>
            <td class="py-3 px-3 text-neutral-800 whitespace-nowrap">
              {{ log.userName || "-" }}
              <span v-if="log.userId" class="text-neutral-300 text-xs">({{ short(log.userId) }})</span>
            </td>
            <td class="py-3 px-3 whitespace-nowrap">
              <span
                class="px-2 py-0.5 rounded text-xs"
                :class="tagClass(log.action)"
                >{{ log.actionName || log.action }}</span
              >
            </td>
            <td class="py-3 px-3 text-neutral-600 max-w-[420px]">
              <div class="line-clamp-2">{{ log.detail || "-" }}</div>
            </td>
            <td class="py-3 px-3 text-neutral-400 text-xs whitespace-nowrap">{{ log.ip || "-" }}</td>
            <td class="py-3 px-3 whitespace-nowrap">
              <span
                class="px-2 py-0.5 rounded text-xs"
                :class="log.result === 'fail' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'"
                >{{ log.result === "fail" ? "失败" : "成功" }}</span
              >
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分页 -->
      <div class="flex items-center justify-between pt-4">
        <span class="text-sm text-neutral-500">共 {{ total }} 条</span>
        <div class="flex items-center gap-2">
          <button
            @click="goPage(page - 1)"
            :disabled="page <= 1"
            class="px-3 py-1.5 rounded border border-neutral-300 text-sm disabled:opacity-40"
          >
            上一页
          </button>
          <span class="text-sm text-neutral-600">{{ page }} / {{ maxPage }}</span>
          <button
            @click="goPage(page + 1)"
            :disabled="page >= maxPage"
            class="px-3 py-1.5 rounded border border-neutral-300 text-sm disabled:opacity-40"
          >
            下一页
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";

export default {
  name: "OperationLog",
  data() {
    return {
      list: [],
      total: 0,
      page: 1,
      pageSize: 20,
      searchKey: "",
      type: "",
      loading: false,
      types: [
        { v: "login", n: "登录" },
        { v: "download", n: "下载工具" },
        { v: "play", n: "在线使用" },
        { v: "comment", n: "发表评论" },
        { v: "feedback", n: "反馈 / 追问" },
        { v: "change_pwd", n: "修改密码" },
        { v: "reset_pwd", n: "管理员重置密码" },
        { v: "find_pwd", n: "找回密码申请" },
      ],
    };
  },
  computed: {
    maxPage() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
  },
  created() {
    this.getList();
  },
  methods: {
    short(id) {
      return id && id.length > 8 ? id.slice(0, 8) + "…" : id;
    },
    tagClass(action) {
      const map = {
        login: "bg-blue-50 text-blue-600",
        download: "bg-purple-50 text-purple-600",
        play: "bg-indigo-50 text-indigo-600",
        comment: "bg-neutral-100 text-neutral-600",
        feedback: "bg-amber-50 text-amber-700",
        change_pwd: "bg-orange-50 text-orange-600",
        reset_pwd: "bg-red-50 text-red-600",
        find_pwd: "bg-red-50 text-red-600",
      };
      return map[action] || "bg-neutral-100 text-neutral-600";
    },
    reload() {
      this.page = 1;
      this.getList();
    },
    goPage(p) {
      if (p < 1 || p > this.maxPage) return;
      this.page = p;
      this.getList();
    },
    async getList() {
      this.loading = true;
      try {
        const res = await this.$axios({
          url: "notice/admin/logs",
          method: "POST",
          data: {
            data: await this.$aesEncrypt({
              page: this.page,
              pageSize: this.pageSize,
              searchKey: this.searchKey,
              type: this.type,
            }),
          },
        });
        if (res.code === 200 && res.data) {
          this.list = res.data.list || [];
          this.total = res.data.total || 0;
        }
      } catch (e) {
        ElMessage({ message: "加载日志失败", type: "error" });
      }
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
